// src/data/mock/testimonials.ts

/**
 * Testimonials Data Structure
 * 
 * Testimonials from parents, players, coaches, and partners about
 * their experience with Champions Academy.
 * 
 * @note Replace placeholder data with actual testimonials
 * @note All images should be placed in /public/images/testimonials/ directory
 */

export interface Testimonial {
  id: string;
  
  // Type of testimonial
  type: 'parent' | 'player' | 'coach' | 'partner' | 'alumni';
  
  // Person details
  name: {
    en: string;
    ar: string;
  };
  
  // Role/Relation
  role: {
    en: string;
    ar: string;
  };
  
  // Image
  image?: string;
  
  // Main testimonial content
  content: {
    en: string;
    ar: string;
  };
  
  // Rating (1-5 stars)
  rating: 1 | 2 | 3 | 4 | 5;
  
  // Date of testimonial
  date?: string;
  
  // Sport/Program they're associated with
  sport?: {
    en: string;
    ar: string;
  };
  
  // Years associated with academy
  yearsWithAcademy?: string;
  
  // Location
  location?: {
    en: string;
    ar: string;
  };
  
  // Is this a featured/highlighted testimonial?
  featured?: boolean;
  
  // Video testimonial URL (if available)
  videoUrl?: string;
  
  // Additional details
  achievements?: {
    en: string[];
    ar: string[];
  };
  
  // Verified testimonial
  verified?: boolean;
}

/**
 * Testimonials Data
 * 
 * TODO: Replace with actual testimonials
 * - Collect real testimonials from parents and players
 * - Get permission to use names and photos
 * - Record video testimonials if possible
 * - Verify all testimonials are authentic
 */
export const testimonials: Testimonial[] = [
  // ==========================================
  // REAL TESTIMONIALS - شهادات حقيقية
  // (Collected from Champions family club members & families)
  // ==========================================
  {
    id: "test-saad-hakoura",
    featured: true,
    verified: true,
    type: "parent",
    name: {
      en: "Saad Hakoura",
      ar: "سعد حاكورة"
    },
    role: {
      en: "Club Member & Father of an Academy Player",
      ar: "عضو في النادي ووالد لاعب في الأكاديمية"
    },
    image: "/img/testimonials/saad-hakoura.jpg",
    rating: 5,
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    content: {
      en: "Yes, and a trillion times yes to Champions — the most prominent and advanced family club in Palestine! The vision, the planning, the execution… all the way to soaring with confidence in the sky of our homeland Palestine, and in Gaza, the beating heart of the south, despite its bleeding wounds and amid life circumstances of extreme difficulty and relentless targeting of every kind. This bold and ambitious step turned pain into hope, and a bleeding wound into a roadmap born of an ambitious, promising national strategy built on standards of refinement and civilization in the face of a harsh lived reality. It proves that achievement, at the speed of lightning, is a powerful force that draws families together and binds them — and the family is the foundational building block of our national social fabric, the solid pillar of the tent whose roots run deep in this good, pure, sacred land. That is why we must encourage and urge belonging to Champions — the strong, cohesive, harmonious family fortress. True belonging widens the circles of solidarity, so that everyone becomes the first and last safety net rallying around Champions: the advanced, ever-renewing family edifice, full of vitality, with our Palestinian Gazan family as the fuel of its movement, its brilliance, and its flight in the sky of the homeland — where the falcons dare to soar.",
      ar: "نعم وتريليون نعم لشامبيونز، النادي العائلي الأبرز والمتطور في فلسطين؟؟!! فالفكرة والتخطيط والتنفيذ… وصولاً للعُلُوّ والتحليق الواثق في سماء الوطن فلسطين، وفي غزة قلب المحافظات الجنوبية النابض، رغم الجرح النازف وفي ظل ظروفٍ حياتية غاية في التعقيد والصعوبة والاستهداف المتعدد الأنماط والأشكال والألوان. هذه الخطوة الطموحة والجريئة صنعت من الألم أملاً، ومن الجرح النازف خريطة طريق نابعة من إستراتيجية وطنية طموحة وواعدة بمعايير الرقي والحضارة في وجه الواقع الصعب المعاش، والتأكيد على أن الإنجاز وفي سرعة البرق يعتبر عامل جذب قوي وتثبيت والتصاق للأسرة، اللبنة الأساس في الكيانية المجتمعية الوطنية التي تعتبر عمود خيمتها الصلب والضارب جذوره بعمق الأرض الطيبة الطاهرة المقدسة. لذلك لابد لنا إلا أن نشجع ونوصي على ضرورة الانتماء لشامبيونز… القلعة العائلية القوية المترابطة المنسجمة. كما أن صدق الانتماء يوسّع دوائر الاصطفاف والتمترس بقوة ليكون الجميع شبكة الأمان الأولى والأخيرة للالتفاف حول شامبيونز، الصرح العائلي المتطور والمتجدد المفعم بكل أنواع الحيوية التي تمثل أسرتنا الفلسطينية الغزّاوية وقود حركته وتألقه وتحليقه في سماء الوطن، حيث تجرؤ الصقور."
    }
  },
  {
    id: "test-nesreen-okasha",
    featured: true,
    verified: true,
    type: "coach",
    name: {
      en: "Nesreen Okasha",
      ar: "نسرين عكاشة"
    },
    role: {
      en: "Physical Education Teacher",
      ar: "مدرّسة تربية رياضية"
    },
    rating: 5,
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    content: {
      en: "A club of a level of grandeur and distinction unmatched in the Gaza Strip in every training aspect. It caters to all age groups, and it has another advantage: it covers every sport. There is no club in Gaza with the level of excellence this club rises to.",
      ar: "نادٍ على مستوى من الفخامة والتميز لا مثيل له بقطاع غزة من جميع النواحي التدريبية، ويهتم بجميع الفئات العمرية، وله ميزة أخرى أنه يتبع جميع الرياضات، ولا يوجد نادٍ بغزة بالتميز الذي يرتقي فيه هذا النادي."
    }
  },
  {
    id: "test-nour-alshawa",
    verified: true,
    type: "player",
    name: {
      en: "Nour Al-Shawa",
      ar: "نور الشوا"
    },
    role: {
      en: "Club Member & Table Tennis Player",
      ar: "عضو في النادي ولاعبة تنس طاولة"
    },
    rating: 5,
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    sport: {
      en: "Table Tennis",
      ar: "تنس الطاولة"
    },
    content: {
      en: "I'm very, very happy that Champions family club exists — it's one of the essentials Gaza was missing. Caring about a woman's right to practice sport is rare in the Gaza Strip, but with Champions nothing is impossible. Thank you all! Mashallah, a high-level club, high-level courts, and they care about the members' needs. We are proud you exist, and God willing you'll go from strength to strength. I warmly salute the people behind this idea, and may God give you wellness for this great effort.",
      ar: "أنا مبسوطة كتير كتير بوجود نادي تشامبيونز العائلي… من الأساسيات اللي كانت ناقصة في غزة. الاهتمام بحقوق المرأة في ممارسة النشاط الرياضي من النادر وجوده في قطاع غزة، بس مع تشامبيونز مافي مستحيل. شكراً إلكم، وماشاءالله نادي على مستوى عالٍ وملاعب على مستوى عالٍ… وبيهتم باحتياجات الأعضاء. بنفتخر بوجودكم وإن شاءالله من تقدم إلى تقدم، وبحيّي جداً أصحاب الفكرة ويعطيكم ألف عافية على هذا المجهود."
    }
  },
  {
    id: "test-rania-almuzaini",
    verified: true,
    type: "parent",
    name: {
      en: "Rania Al Muzaini",
      ar: "رانيا المزيني"
    },
    role: {
      en: "Mother of a Player & Club Member",
      ar: "أم لاعب وعضو في النادي"
    },
    rating: 5,
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    content: {
      en: "Champions doesn't only provide football training, it also encourages good manners and a positive attitude. Happy with their service.",
      ar: "تشامبيونز لا يقدّم تدريب كرة القدم فحسب، بل يشجّع أيضاً على الأخلاق الحميدة والسلوك الإيجابي. أنا سعيدة بخدماتهم."
    }
  },

  // ==========================================
  // FEATURED TESTIMONIALS - شهادات مميزة
  // ==========================================
  {
    id: "test-featured-001",
    featured: true,
    verified: true,
    type: "parent",
    name: {
      en: "Dr. Fatima Al-Masri",
      ar: "د. فاطمة المصري"
    },
    role: {
      en: "Mother of 3 Academy Athletes",
      ar: "أم لـ 3 رياضيين في الأكاديمية"
    },
    image: "/assets/images/s-blob-v1-IMAGE-wA5xAwin9fo.jpeg",
    rating: 5,
    date: "2024-01-15",
    location: {
      en: "Gaza City",
      ar: "مدينة غزة"
    },
    yearsWithAcademy: "2018 - Present",
    content: {
      en: "Champions Academy has been a transformative force in my children's lives. All three of my kids train here - football, swimming, and karate. What sets this academy apart is not just the world-class training, but the values they instill: discipline, respect, teamwork, and perseverance. The coaches are like family, and the facilities are exceptional. I've watched my children grow not just as athletes, but as confident, well-rounded individuals. This is more than a sports academy - it's a community that nurtures dreams and builds character.",
      ar: "كانت أكاديمية تشامبيونز قوة تحويلية في حياة أطفالي. جميع أطفالي الثلاثة يتدربون هنا - كرة القدم، السباحة، والكاراتيه. ما يميز هذه الأكاديمية ليس فقط التدريب على مستوى عالمي، بل القيم التي يغرسونها: الانضباط، الاحترام، العمل الجماعي، والمثابرة. المدربون مثل العائلة، والمرافق استثنائية. شاهدت أطفالي ينمون ليس فقط كرياضيين، بل كأفراد واثقين ومتكاملين. هذه أكثر من أكاديمية رياضية - إنها مجتمع يرعى الأحلام ويبني الشخصية."
    },
    sport: {
      en: "Multi-Sport",
      ar: "رياضات متعددة"
    },
    achievements: {
      en: [
        "All 3 children represented in regional competitions",
        "Eldest son earned football scholarship",
        "Daughter is national swimming champion"
      ],
      ar: [
        "جميع الأطفال الـ 3 يمثلون في المنافسات الإقليمية",
        "الابن الأكبر حصل على منحة كرة قدم",
        "الابنة بطلة السباحة الوطنية"
      ]
    }
  },

  // ==========================================
  // PARENT TESTIMONIALS - شهادات الأهل
  // ==========================================
  {
    id: "test-parent-001",
    type: "parent",
    verified: true,
    name: {
      en: "Ahmed Khalil",
      ar: "أحمد خليل"
    },
    role: {
      en: "Father of U-14 Football Player",
      ar: "والد لاعب كرة قدم تحت 14"
    },
    image: "/assets/team/s-blob-v1-IMAGE-AdrJGHuqoHQ.jpeg",
    rating: 5,
    date: "2024-02-10",
    location: {
      en: "Jabalia",
      ar: "جباليا"
    },
    yearsWithAcademy: "2020 - Present",
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    content: {
      en: "My son joined Champions Academy when he was 11. The change has been remarkable. His technical skills improved dramatically, but more importantly, his confidence and discipline transformed. The coaches genuinely care about each child's development. The academy provided him with opportunities to compete at higher levels, and now he's being scouted by professional clubs. This academy gave my son a future in football.",
      ar: "انضم ابني إلى أكاديمية تشامبيونز عندما كان عمره 11 عاماً. التغيير كان ملحوظاً. تحسنت مهاراته التقنية بشكل كبير، ولكن الأهم من ذلك، تحولت ثقته وانضباطه. المدربون يهتمون حقاً بتطوير كل طفل. وفرت له الأكاديمية فرصاً للمنافسة على مستويات أعلى، والآن يتم متابعته من قبل أندية محترفة. هذه الأكاديمية أعطت ابني مستقبلاً في كرة القدم."
    }
  },
  {
    id: "test-parent-002",
    type: "parent",
    verified: true,
    name: {
      en: "Layla Hassan",
      ar: "ليلى حسن"
    },
    role: {
      en: "Mother of Twin Swimmers",
      ar: "أم لتوأمين سباحين"
    },
    rating: 5,
    date: "2023-11-20",
    location: {
      en: "Khan Younis",
      ar: "خان يونس"
    },
    yearsWithAcademy: "2019 - Present",
    sport: {
      en: "Swimming",
      ar: "السباحة"
    },
    content: {
      en: "The swimming program at Champions Academy is outstanding. My twins started as beginners and are now competing nationally. The instructors are certified, patient, and create a safe, encouraging environment. The academy's commitment to both boys and girls is admirable. I'm grateful for the positive impact on my children's health, confidence, and athletic abilities.",
      ar: "برنامج السباحة في أكاديمية تشامبيونز رائع. بدأ توأماي كمبتدئين والآن يتنافسان على المستوى الوطني. المدربون معتمدون، صبورون، ويخلقون بيئة آمنة ومشجعة. التزام الأكاديمية بكل من الأولاد والبنات يستحق الإعجاب. أنا ممتنة للتأثير الإيجابي على صحة أطفالي وثقتهم وقدراتهم الرياضية."
    }
  },
  {
    id: "test-parent-003",
    type: "parent",
    verified: true,
    name: {
      en: "Mahmoud Abu Salem",
      ar: "محمود أبو سالم"
    },
    role: {
      en: "Father of Young Athlete",
      ar: "والد رياضي شاب"
    },
    image: "/assets/team/s-blob-v1-IMAGE-ErIcjKSeec4.jpeg",
    rating: 5,
    date: "2024-01-05",
    location: {
      en: "Rafah",
      ar: "رفح"
    },
    yearsWithAcademy: "2021 - Present",
    sport: {
      en: "Basketball",
      ar: "كرة السلة"
    },
    content: {
      en: "Champions Academy is a beacon of hope. Despite all challenges, they continue to provide world-class training and a safe space for our children. My son looks forward to training every day. The academy teaches life skills alongside sports - leadership, teamwork, resilience. I recommend it to every parent who wants their child to excel in sports and in life.",
      ar: "أكاديمية تشامبيونز منارة أمل. على الرغم من جميع التحديات، يواصلون تقديم تدريب على مستوى عالمي ومساحة آمنة لأطفالنا. ابني يتطلع للتدريب كل يوم. تعلم الأكاديمية مهارات الحياة جنباً إلى جنب مع الرياضة - القيادة، العمل الجماعي، المرونة. أوصي بها لكل والد يريد أن يتفوق طفله في الرياضة وفي الحياة."
    }
  },
  {
    id: "test-parent-004",
    type: "parent",
    verified: true,
    name: {
      en: "Nour Mansour",
      ar: "نور منصور"
    },
    role: {
      en: "Mother of Karate Student",
      ar: "أم لطالبة كاراتيه"
    },
    rating: 5,
    date: "2023-12-15",
    location: {
      en: "Deir al-Balah",
      ar: "دير البلح"
    },
    yearsWithAcademy: "2020 - Present",
    sport: {
      en: "Karate",
      ar: "الكاراتيه"
    },
    content: {
      en: "My daughter was shy and lacked confidence. Karate at Champions Academy completely transformed her. She earned her black belt and now teaches younger students. The discipline, focus, and self-confidence she gained are invaluable. The academy doesn't just train athletes - they build strong, capable individuals.",
      ar: "كانت ابنتي خجولة وتفتقر إلى الثقة. الكاراتيه في أكاديمية تشامبيونز حولتها تماماً. حصلت على الحزام الأسود والآن تدرّس الطلاب الأصغر سناً. الانضباط والتركيز والثقة بالنفس التي اكتسبتها لا تقدر بثمن. الأكاديمية لا تدرب الرياضيين فقط - إنها تبني أفراداً أقوياء وقادرين."
    }
  },

  // ==========================================
  // PLAYER TESTIMONIALS - شهادات اللاعبين
  // ==========================================
  {
    id: "test-player-001",
    type: "player",
    verified: true,
    name: {
      en: "Youssef Ahmad",
      ar: "يوسف أحمد"
    },
    role: {
      en: "U-16 Football Captain",
      ar: "قائد كرة القدم تحت 16"
    },
    image: "/assets/images/s-blob-v1-IMAGE-feT8ETn1U54.jpeg",
    rating: 5,
    date: "2024-02-01",
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    yearsWithAcademy: "2018 - Present",
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    content: {
      en: "Champions Academy is my second home. The coaches push us to be our best while treating us with respect and care. I've learned so much about football tactics, fitness, and mental strength. Being team captain taught me leadership skills I use everywhere. This academy prepared me for professional football and for life. I'm forever grateful.",
      ar: "أكاديمية تشامبيونز هي بيتي الثاني. المدربون يدفعوننا لنكون في أفضل حالاتنا بينما يعاملوننا باحترام ورعاية. تعلمت الكثير عن تكتيكات كرة القدم واللياقة البدنية والقوة العقلية. كوني قائد الفريق علمني مهارات القيادة التي أستخدمها في كل مكان. هذه الأكاديمية أعدتني لكرة القدم الاحترافية وللحياة. أنا ممتن للأبد."
    }
  },
  {
    id: "test-player-002",
    type: "player",
    verified: true,
    name: {
      en: "Sara Khalil",
      ar: "سارة خليل"
    },
    role: {
      en: "Junior Swimming Champion",
      ar: "بطلة السباحة للناشئين"
    },
    rating: 5,
    date: "2023-12-28",
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    yearsWithAcademy: "2017 - Present",
    sport: {
      en: "Swimming",
      ar: "السباحة"
    },
    content: {
      en: "I started swimming at Champions Academy when I was 9. Now I'm a national champion and represent Palestine internationally. The training is intense but the support is incredible. My coaches believed in me even when I doubted myself. This academy gave me the tools to succeed and the confidence to dream big. Thank you, Champions Academy!",
      ar: "بدأت السباحة في أكاديمية تشامبيونز عندما كان عمري 9 سنوات. الآن أنا بطلة وطنية وأمثل فلسطين دولياً. التدريب مكثف لكن الدعم لا يصدق. مدربوني آمنوا بي حتى عندما شككت بنفسي. هذه الأكاديمية أعطتني الأدوات للنجاح والثقة للحلم بشكل كبير. شكراً، أكاديمية تشامبيونز!"
    }
  },
  {
    id: "test-player-003",
    type: "player",
    verified: true,
    name: {
      en: "Omar Faraj",
      ar: "عمر فرج"
    },
    role: {
      en: "Basketball Point Guard",
      ar: "صانع ألعاب كرة السلة"
    },
    image: "/assets/images/s-blob-v1-IMAGE-GrqwWS_efRQ.jpeg",
    rating: 5,
    date: "2024-01-18",
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    yearsWithAcademy: "2016 - Present",
    sport: {
      en: "Basketball",
      ar: "كرة السلة"
    },
    content: {
      en: "Champions Academy doesn't just teach you how to play - they teach you how to think, compete, and win. The basketball program is top-notch with professional training methods. I've improved my skills, court IQ, and physical fitness dramatically. The best part? The brotherhood we've built as a team. This place is special.",
      ar: "أكاديمية تشامبيونز لا تعلمك فقط كيف تلعب - إنها تعلمك كيف تفكر، تنافس، وتفوز. برنامج كرة السلة من الدرجة الأولى مع أساليب تدريب احترافية. حسّنت مهاراتي وذكاء الملعب واللياقة البدنية بشكل كبير. الجزء الأفضل؟ الأخوة التي بنيناها كفريق. هذا المكان مميز."
    }
  },
  {
    id: "test-player-004",
    type: "player",
    verified: true,
    name: {
      en: "Aya Ibrahim",
      ar: "آية إبراهيم"
    },
    role: {
      en: "Karate Black Belt",
      ar: "حزام أسود كاراتيه"
    },
    rating: 5,
    date: "2023-11-30",
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    yearsWithAcademy: "2018 - Present",
    sport: {
      en: "Karate",
      ar: "الكاراتيه"
    },
    content: {
      en: "Karate at Champions Academy taught me discipline, respect, and self-defense. Earning my black belt was the proudest moment of my life. The sensei are patient, knowledgeable, and inspiring. I've competed regionally and won medals, but the real victory is the person I've become through martial arts training here.",
      ar: "الكاراتيه في أكاديمية تشامبيونز علمتني الانضباط والاحترام والدفاع عن النفس. الحصول على الحزام الأسود كان أفخر لحظة في حياتي. السنسي صبورون ومعرفيون وملهمون. تنافست إقليمياً وفزت بميداليات، لكن الفوز الحقيقي هو الشخص الذي أصبحته من خلال تدريب الفنون القتالية هنا."
    }
  },
  {
    id: "test-player-005",
    type: "player",
    verified: true,
    name: {
      en: "Khaled Naser",
      ar: "خالد ناصر"
    },
    role: {
      en: "Table Tennis Champion",
      ar: "بطل تنس الطاولة"
    },
    rating: 5,
    date: "2024-02-05",
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    yearsWithAcademy: "2019 - Present",
    sport: {
      en: "Table Tennis",
      ar: "تنس الطاولة"
    },
    content: {
      en: "I discovered my passion for table tennis at Champions Academy. The coaches saw my potential and nurtured it with expert training and constant encouragement. Now I'm ranked #1 in Palestine and competing internationally. This academy gave me a sport I love and a path to success. The journey continues!",
      ar: "اكتشفت شغفي بتنس الطاولة في أكاديمية تشامبيونز. المدربون رأوا إمكاناتي ورعوها بتدريب خبير وتشجيع مستمر. الآن أنا مصنف #1 في فلسطين وأتنافس دولياً. هذه الأكاديمية أعطتني رياضة أحبها ومساراً للنجاح. الرحلة مستمرة!"
    }
  },

  // ==========================================
  // ALUMNI TESTIMONIALS - شهادات الخريجين
  // ==========================================
  {
    id: "test-alumni-001",
    type: "alumni",
    verified: true,
    name: {
      en: "Mohamed Al-Khalidi",
      ar: "محمد الخالدي"
    },
    role: {
      en: "Professional Footballer",
      ar: "لاعب كرة قدم محترف"
    },
    image: "/assets/images/s-blob-v1-IMAGE-56VzkL9pHSw.jpeg",
    rating: 5,
    date: "2023-10-20",
    location: {
      en: "Egypt (Originally Gaza)",
      ar: "مصر (أصلاً من غزة)"
    },
    yearsWithAcademy: "2014 - 2018",
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    content: {
      en: "Champions Academy was the foundation of my professional career. The technical training, tactical education, and mental preparation I received there prepared me for the demands of professional football. I'm now playing at a top club, but I'll never forget where it all started. Thank you to all my coaches who believed in a kid from Gaza with big dreams.",
      ar: "كانت أكاديمية تشامبيونز أساس مسيرتي المهنية. التدريب التقني والتعليم التكتيكي والإعداد العقلي الذي تلقيته هناك أعدني لمتطلبات كرة القدم الاحترافية. أنا الآن ألعب في نادٍ كبير، لكنني لن أنسى أبداً من أين بدأ كل شيء. شكراً لجميع مدربيني الذين آمنوا بطفل من غزة لديه أحلام كبيرة."
    }
  },
  {
    id: "test-alumni-002",
    type: "alumni",
    verified: true,
    name: {
      en: "Layla Mansour",
      ar: "ليلى منصور"
    },
    role: {
      en: "National Team Swimmer & Coach",
      ar: "سباحة منتخب وطني ومدربة"
    },
    rating: 5,
    date: "2023-12-10",
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    yearsWithAcademy: "2015 - 2021",
    sport: {
      en: "Swimming",
      ar: "السباحة"
    },
    content: {
      en: "From student to national champion to coach - Champions Academy has been my entire athletic journey. The values, skills, and confidence I gained here shaped who I am today. Now I'm back as a coach, passing on what I learned to the next generation. This academy changes lives, and I'm living proof.",
      ar: "من طالبة إلى بطلة وطنية إلى مدربة - أكاديمية تشامبيونز كانت رحلتي الرياضية بأكملها. القيم والمهارات والثقة التي اكتسبتها هنا شكلت من أنا اليوم. الآن أنا عدت كمدربة، أنقل ما تعلمته للجيل القادم. هذه الأكاديمية تغير الحيوات، وأنا دليل حي."
    }
  },

  // ==========================================
  // COACH TESTIMONIALS - شهادات المدربين
  // ==========================================
  {
    id: "test-coach-001",
    type: "coach",
    verified: true,
    name: {
      en: "Coach Tariq Hamdan",
      ar: "المدرب طارق حمدان"
    },
    role: {
      en: "Head Football Coach",
      ar: "مدرب كرة القدم الرئيسي"
    },
    image: "/assets/team/s-blob-v1-IMAGE-R4CbZ9_2kRc.jpeg",
    rating: 5,
    date: "2024-01-25",
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    yearsWithAcademy: "2012 - Present",
    sport: {
      en: "Football",
      ar: "كرة القدم"
    },
    content: {
      en: "I've been coaching at Champions Academy for over a decade. What makes this place special is the commitment to holistic development - we don't just build athletes, we build character. Seeing young players grow, achieve their dreams, and become role models is the greatest reward. This academy has the best facilities, management, and most importantly, the most passionate young athletes in Palestine.",
      ar: "أدرب في أكاديمية تشامبيونز منذ أكثر من عقد. ما يجعل هذا المكان مميزاً هو الالتزام بالتطوير الشامل - نحن لا نبني رياضيين فقط، نحن نبني الشخصية. رؤية اللاعبين الشباب ينمون ويحققون أحلامهم ويصبحون قدوة هو أعظم مكافأة. هذه الأكاديمية لديها أفضل المرافق والإدارة، والأهم من ذلك، أكثر الرياضيين الشباب شغفاً في فلسطين."
    }
  },

  // ==========================================
  // PARTNER TESTIMONIALS - شهادات الشركاء
  // ==========================================
  {
    id: "test-partner-001",
    type: "partner",
    verified: true,
    name: {
      en: "Dr. Hassan Al-Najjar",
      ar: "د. حسن النجار"
    },
    role: {
      en: "Sports Medicine Specialist",
      ar: "أخصائي طب رياضي"
    },
    rating: 5,
    date: "2023-11-15",
    location: {
      en: "Gaza",
      ar: "غزة"
    },
    yearsWithAcademy: "2017 - Present",
    content: {
      en: "As a sports medicine partner with Champions Academy, I'm impressed by their professional approach to athlete health and safety. They prioritize proper warm-ups, injury prevention, and recovery protocols. The management is responsive and always puts the athletes' wellbeing first. It's a pleasure working with such a dedicated organization.",
      ar: "كشريك طب رياضي مع أكاديمية تشامبيونز، أنا معجب بنهجهم المهني لصحة وسلامة الرياضيين. يعطون الأولوية للإحماء المناسب والوقاية من الإصابات وبروتوكولات التعافي. الإدارة متجاوبة ودائماً تضع رفاهية الرياضيين أولاً. إنه لمن دواعي سروري العمل مع منظمة متفانية كهذه."
    }
  },

];

/**
 * Utility Functions
 */

// Get testimonials by type
export const getTestimonialsByType = (type: Testimonial['type']): Testimonial[] => {
  return testimonials.filter(t => t.type === type);
};

// Get featured testimonials
export const getFeaturedTestimonials = (): Testimonial[] => {
  return testimonials.filter(t => t.featured);
};

// Get verified testimonials
export const getVerifiedTestimonials = (): Testimonial[] => {
  return testimonials.filter(t => t.verified);
};

// Get testimonials by rating
export const getTestimonialsByRating = (rating: number): Testimonial[] => {
  return testimonials.filter(t => t.rating >= rating);
};

// Get testimonials by sport
export const getTestimonialsBySport = (sport: string): Testimonial[] => {
  return testimonials.filter(t => 
    t.sport?.en.toLowerCase().includes(sport.toLowerCase())
  );
};

// Calculate average rating
export const getAverageRating = (): number => {
  const sum = testimonials.reduce((acc, t) => acc + t.rating, 0);
  return sum / testimonials.length;
};

/**
 * Statistics
 */
export const testimonialsStats = {
  total: testimonials.length,
  byType: {
    parent: testimonials.filter(t => t.type === 'parent').length,
    player: testimonials.filter(t => t.type === 'player').length,
    coach: testimonials.filter(t => t.type === 'coach').length,
    partner: testimonials.filter(t => t.type === 'partner').length,
    alumni: testimonials.filter(t => t.type === 'alumni').length,
  },
  featured: testimonials.filter(t => t.featured).length,
  verified: testimonials.filter(t => t.verified).length,
  averageRating: getAverageRating(),
  fiveStars: testimonials.filter(t => t.rating === 5).length,
};
